'use client'

import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useToaster } from '../context/ToasterContext';
import Spinner from "../components/Spinner";
import Button from '../components/Button';
import Table from '../components/Table';
import ConfirmationModal from '../components/ConfirmationModal';
import { useDispatch, useSelector } from 'react-redux';
import { fetchSenders } from '../redux/globalDataSlice';
import api from '../api/axios';

const DEFAULT_DESCRIPTION = 'Are you sure you want to delete? This action cannot be undone.';
const INPUT_CLS = 'rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 px-3 py-1.5 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500';
const EMPTY_FILTERS = { name: '', type: '', number: '', connectorName: '', active: '' };

export default function Senders() {
  const { toaster } = useToaster();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [modalDescription, setModalDescription] = useState(DEFAULT_DESCRIPTION);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const { senders = [], connectors = [], dataLoading } = useSelector((s) => s.globalData);
  const activeAccountId = useSelector((s) => s.globalData.activeAccountId);

  useEffect(() => { setLoading(dataLoading); }, [dataLoading]);

  // Enrich senders with a derived _connectorName for filtering/sorting
  const enriched = useMemo(() => senders.map(s => ({
    ...s,
    _connectorName: connectors.find(c => c.id === s.vendorAccountId)?.connectorName ?? '',
  })), [senders, connectors]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const hasFilters = Object.values(filters).some(Boolean);

  const filteredAndSorted = useMemo(() => {
    let result = [...enriched];

    if (filters.name) {
      result = result.filter(s => s.name?.toLowerCase().includes(filters.name.toLowerCase()));
    }
    if (filters.type) {
      result = result.filter(s => s.type?.toLowerCase().includes(filters.type.toLowerCase()));
    }
    if (filters.number) {
      result = result.filter(s => s.number?.includes(filters.number));
    }
    if (filters.connectorName) {
      result = result.filter(s =>
        s._connectorName.toLowerCase().includes(filters.connectorName.toLowerCase())
      );
    }
    if (filters.active !== '') {
      const isActive = filters.active === 'true';
      result = result.filter(s => !!s.active === isActive);
    }

    if (sortConfig.key) {
      result.sort((a, b) => {
        const aVal = String(a[sortConfig.key] ?? '');
        const bVal = String(b[sortConfig.key] ?? '');
        // Numeric sort for rate
        if (sortConfig.key === 'rate') {
          const cmp = Number(a.rate ?? 0) - Number(b.rate ?? 0);
          return sortConfig.direction === 'asc' ? cmp : -cmp;
        }
        const cmp = aVal.localeCompare(bVal);
        return sortConfig.direction === 'asc' ? cmp : -cmp;
      });
    }

    return result;
  }, [enriched, filters, sortConfig]);

  const initDeleteSender = (itemData) => {
    setModalDescription(DEFAULT_DESCRIPTION);
    setSelectedItem(itemData);
    setShowConfirmationModal(true);
  };

  const handleCancel = () => {
    setShowConfirmationModal(false);
    setModalDescription(DEFAULT_DESCRIPTION);
  };

  const deleteSender = async () => {
    try {
      await api.delete(`${process.env.REACT_APP_BASE_API_URL}/mvapi/utils/senders/account/${activeAccountId}/${selectedItem.id}`);
      toaster.success('Sender deleted successfully!');
      if (activeAccountId) await dispatch(fetchSenders(activeAccountId));
      setShowConfirmationModal(false);
    } catch (error) {
      if (error.response?.status === 409) {
        setModalDescription(
          <span>
            This sender is assigned to template{' '}
            <Link
              to={`/edit-template/${selectedItem.templateId}`}
              className="text-blue-600 dark:text-blue-400 underline font-medium"
              onClick={handleCancel}
            >
              {selectedItem.templateId}
            </Link>
            . Remove it from the template before deleting.
          </span>
        );
      } else {
        toaster.error('Failed to delete sender. Please try again.');
        setShowConfirmationModal(false);
      }
    }
  };

  return (
    <>
      {loading && <Spinner />}

      {!loading && (
        <>
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="sm:flex sm:items-center">
              <div className="sm:flex-auto">
                <h2 className="text-2xl/7 font-bold text-gray-900 dark:text-gray-100 sm:truncate sm:text-3xl sm:tracking-tight">
                  Senders
                </h2>
                <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">
                  Manage all your senders here
                </p>
              </div>
              <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
                <Button type="button" variant="primary" onClick={() => navigate('/add-sender')}>
                  Create new sender
                </Button>
              </div>
            </div>

            {/* Filter bar */}
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <input
                type="text"
                placeholder="Filter by name…"
                value={filters.name}
                onChange={e => setFilters(f => ({ ...f, name: e.target.value }))}
                className={INPUT_CLS}
              />
              <input
                type="text"
                placeholder="Filter by type…"
                value={filters.type}
                onChange={e => setFilters(f => ({ ...f, type: e.target.value }))}
                className={INPUT_CLS}
              />
              <input
                type="text"
                placeholder="Filter by number…"
                value={filters.number}
                onChange={e => setFilters(f => ({ ...f, number: e.target.value }))}
                className={INPUT_CLS}
              />
              <input
                type="text"
                placeholder="Filter by connector…"
                value={filters.connectorName}
                onChange={e => setFilters(f => ({ ...f, connectorName: e.target.value }))}
                className={INPUT_CLS}
              />
              <select
                value={filters.active}
                onChange={e => setFilters(f => ({ ...f, active: e.target.value }))}
                className={INPUT_CLS}
              >
                <option value="">All statuses</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
              {hasFilters && (
                <button
                  type="button"
                  onClick={() => setFilters(EMPTY_FILTERS)}
                  className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 underline"
                >
                  Clear filters
                </button>
              )}
            </div>

            <div className="mt-4">
              <Table
                data={filteredAndSorted}
                sortConfig={sortConfig}
                onSort={handleSort}
                onRowClick={(sender) => navigate(`/edit-sender/${sender.id}`)}
                columns={[
                  { key: 'name', title: 'Sender name', sortable: true },
                  { key: 'type', title: 'Type', sortable: true },
                  { key: 'number', title: 'Number', sortable: true },
                  {
                    key: '_connectorName',
                    title: 'Connector name',
                    sortable: true,
                    render: (sender) => sender._connectorName || '—',
                  },
                  { key: 'notes', title: 'Notes' },
                  { key: 'active', title: 'Active', sortable: true, condition: true, trueValue: 'Active', falseValue: 'Inactive' },
                  { key: 'rate', title: 'Rate limit (msg/s)', sortable: true },
                  {
                    key: 'actions',
                    title: 'Actions',
                    align: 'right',
                    render: (sender) => (
                      <div className="space-x-2">
                        <Link
                          to={`/edit-sender/${sender.id}`}
                          className="text-emerald-600 dark:text-emerald-400 hover:text-emerald-900 dark:hover:text-emerald-100 mr-5"
                        >
                          Edit
                        </Link>
                        <button
                          type="button"
                          onClick={() => initDeleteSender(sender)}
                          className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-100"
                        >
                          Delete
                        </button>
                      </div>
                    ),
                  },
                ]}
                emptyState={{
                  title: hasFilters ? 'No senders match your filters' : 'No senders found',
                  description: hasFilters ? 'Try adjusting or clearing the filters.' : 'Get started by creating a new sender.',
                }}
              />
            </div>
          </div>
        </>
      )}

      <ConfirmationModal
        open={showConfirmationModal}
        title="Delete sender"
        description={modalDescription}
        confirmText="Yes"
        cancelText="No"
        onConfirm={deleteSender}
        onCancel={handleCancel}
      />
    </>
  );
}
